import React from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/CloseRounded';

import type { ILeadLabel } from 'src/services/api';

import styles from 'src/components/lead-labels/LeadLabelChips.module.css';

type LabelLike = Pick<ILeadLabel, 'id' | 'name' | 'color'>;

type Props = {
    labels: LabelLike[];
    maxVisible?: number;
    size?: 'compact' | 'body';
    multiline?: boolean;
};

type LeadLabelChipProps = {
    label: Pick<ILeadLabel, 'name' | 'color'>;
    onDelete?: () => void;
    size?: 'compact' | 'body';
};

export function LeadLabelChip({ label, onDelete, size = 'compact' }: LeadLabelChipProps) {
    const isBodySize = size === 'body';

    return (
        <div
            className={`${styles.label} ${isBodySize ? styles.labelBody : ''}`}
            style={{ '--lead-label-color': label.color } as React.CSSProperties}
        >
            <div className={styles.pin} />
            <Typography variant="caption" className={`${styles.text} ${isBodySize ? styles.textBody : ''}`}>
                {label.name}
            </Typography>
            {!!onDelete && (
                <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={(event) => {
                        event.stopPropagation();
                        onDelete();
                    }}
                    aria-label={`Remove ${label.name}`}
                >
                    <CloseIcon className={styles.deleteIcon} />
                </button>
            )}
        </div>
    );
}

export default function LeadLabelChips({ labels, maxVisible = 2, size = 'compact', multiline = false }: Props) {
    const visibleLabels = labels.slice(0, maxVisible);
    const hiddenLabels = labels.slice(maxVisible);
    const numRemainingLabels = labels.length - visibleLabels.length;
    const isBodySize = size === 'body';

    if (labels.length === 0) {
        return null;
    }

    return (
        <div className={`${styles.root} ${multiline ? styles.multiline : ''}`}>
            {visibleLabels.map((label) => <LeadLabelChip key={label.id} label={label} size={size} />)}

            {numRemainingLabels > 0 && (
                <Tooltip title={hiddenLabels.map((label) => label.name).join(', ')}>
                    <div
                        className={`${styles.remaining} ${isBodySize ? styles.remainingBody : ''}`}
                        style={{ '--lead-label-color': hiddenLabels[0].color } as React.CSSProperties}
                    >
                        <Typography variant="caption" className={styles.text}>
                            +{numRemainingLabels}
                        </Typography>
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
